import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Building2, Loader2, Save, CheckCircle2, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { PageHeader } from "./profiel";

export const Route = createFileRoute("/_authenticated/account/zakelijk")({
  head: () => ({
    meta: [
      { title: "Bedrijfsgegevens — Elvion Group Nederland" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: BusinessPage,
});

type BusinessDetails = {
  user_id: string;
  company_name: string;
  kvk_number: string;
  vat_number: string | null;
  approved_at: string | null;
};

function BusinessPage() {
  const { user, profile } = useAuth();
  const [companyName, setCompanyName] = useState("");
  const [kvk, setKvk] = useState("");
  const [vat, setVat] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const { data, refetch, isLoading } = useQuery({
    queryKey: ["my-business"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("business_details")
        .select("user_id, company_name, kvk_number, vat_number, approved_at")
        .eq("user_id", user!.id)
        .maybeSingle<BusinessDetails>();
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (data) {
      setCompanyName(data.company_name);
      setKvk(data.kvk_number);
      setVat(data.vat_number ?? "");
    }
  }, [data]);

  if (profile?.customer_type !== "business") {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={Building2}
          title="Bedrijfsgegevens"
          subtitle="Beschikbaar voor zakelijke accounts."
        />
        <div className="rounded-2xl border border-border bg-card p-8 text-center">
          <p className="text-sm text-text-secondary">
            Uw account is geregistreerd als particulier. Heeft u een bedrijf? Neem dan{" "}
            <Link to="/klantenservice" className="font-semibold text-[#3a7bd5] hover:underline">
              contact
            </Link>{" "}
            met ons op om uw account om te zetten.
          </p>
        </div>
      </div>
    );
  }

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    const payload = {
      user_id: user!.id,
      company_name: companyName.trim(),
      kvk_number: kvk.trim(),
      vat_number: vat.trim() || null,
    };
    const { error } = data
      ? await supabase.from("business_details").update(payload).eq("user_id", user!.id)
      : await supabase.from("business_details").insert(payload);
    setSaving(false);
    if (error) {
      setMsg({ kind: "err", text: error.message });
      return;
    }
    setMsg({ kind: "ok", text: "Bedrijfsgegevens opgeslagen." });
    refetch();
    setTimeout(() => setMsg(null), 2500);
  };

  return (
    <div className="space-y-8">
      <PageHeader
        icon={Building2}
        title="Bedrijfsgegevens"
        subtitle="Beheer uw bedrijfs-, KvK- en btw-gegevens."
      />

      {profile?.verification_status === "approved" ? (
        <div className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-3 text-sm text-emerald-900">
          <CheckCircle2 className="size-5 shrink-0" />
          Uw zakelijke account is geverifieerd.
        </div>
      ) : profile?.verification_status === "pending" ? (
        <div className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-3 text-sm text-amber-900">
          <Clock className="size-5 shrink-0" />
          Uw gegevens worden gecontroleerd. U ontvangt bericht zodra de verificatie is afgerond.
        </div>
      ) : null}

      {isLoading ? (
        <div className="h-64 animate-pulse rounded-2xl bg-muted" />
      ) : (
        <form
          onSubmit={save}
          className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8"
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Bedrijfsnaam" className="sm:col-span-2">
              <input
                required
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className={input}
              />
            </Field>
            <Field label="KvK-nummer">
              <input
                required
                value={kvk}
                onChange={(e) => setKvk(e.target.value)}
                className={input}
                placeholder="12345678"
              />
            </Field>
            <Field label="Btw-nummer (optioneel)">
              <input
                value={vat}
                onChange={(e) => setVat(e.target.value)}
                className={input}
                placeholder="NL123456789B01"
              />
            </Field>
          </div>
          <div className="mt-6 flex items-center justify-between gap-3 border-t border-border pt-5">
            {msg ? (
              <p
                className={`text-sm font-medium ${
                  msg.kind === "ok" ? "text-emerald-600" : "text-red-600"
                }`}
              >
                {msg.text}
              </p>
            ) : (
              <span className="text-xs text-text-secondary">
                Wijzigingen kunnen opnieuw verificatie vereisen.
              </span>
            )}
            <Button type="submit" disabled={saving} className="gap-2">
              {saving ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Save className="size-4" />
              )}
              Opslaan
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

const input =
  "w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm focus:border-[#3a7bd5] focus:outline-none focus:ring-2 focus:ring-[#3a7bd5]/20";

function Field({
  label,
  children,
  className = "",
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-text-secondary">
        {label}
      </span>
      {children}
    </label>
  );
}
